<template>
  <Card v-if="shouldShowCardRef" class="p-4 gap-3" data-test-id="profile-completion-card">
    <CardContent class="p-0 flex flex-col gap-3">
      <span class="text-sm text-muted-foreground">{{ t('profileCompletion.profileLevel', { level: '' }) }}<strong class="text-foreground">{{ profileLevelRef }}</strong></span>
      <div class="flex w-full gap-1">
        <div
          v-for="(_, index) in stepsRef"
          :key="index"
          class="h-2 flex-1 rounded-full transition-all"
          :class="index < completedCountRef ? 'bg-primary' : 'bg-muted'"
        />
      </div>
      <p class="text-xs text-muted-foreground">{{ t('profileCompletion.description') }}</p>
    </CardContent>
    <CardFooter v-if="nextStepRef" class="p-0">
      <div class="flex items-center justify-between w-full gap-3 rounded-lg bg-muted p-3">
        <div class="flex items-center gap-2">
          <CircleCheckIcon class="size-4 text-muted-foreground shrink-0" />
          <div class="flex flex-col">
            <span class="text-xs text-muted-foreground">{{ t('profileCompletion.nextStep') }}</span>
            <span class="text-sm font-medium" data-test-id="profile-completion-next-step">{{
              t(nextStepRef.labelKey)
            }}</span>
          </div>
        </div>
        <Button size="sm" class="shrink-0" data-test-id="profile-completion-next-step-button" @click="handleOpenModal">
          {{ t(nextStepRef.buttonLabelKey) }}
        </Button>
      </div>
    </CardFooter>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CircleCheckIcon } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProfileCompletion from '@/features/users/hooks/use-profile-completion.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const {
  stepsRef,
  nextStepRef,
  shouldShowCardRef,
} = useProfileCompletion()

const PROFILE_LEVEL_KEYS = [
  'profileCompletion.levels.beginner',
  'profileCompletion.levels.basic',
  'profileCompletion.levels.intermediate',
  'profileCompletion.levels.advanced',
  'profileCompletion.levels.expert',
] as const

const completedCountRef = computed(() => stepsRef.value.filter((s) => s.completed).length)

const profileLevelRef = computed(() => {
  const completedCount = completedCountRef.value
  const total = stepsRef.value.length
  if (total === 0) return t(PROFILE_LEVEL_KEYS[PROFILE_LEVEL_KEYS.length - 1])
  const index = Math.min(completedCount, PROFILE_LEVEL_KEYS.length - 1)
  return t(PROFILE_LEVEL_KEYS[index])
})

const handleOpenModal = () => {
  if (!nextStepRef.value) return

  const { modal, modalData } = nextStepRef.value

  if (modalData) {
    modalsStore.updateModalAttachedData(modal, modalData)
  }

  modalsStore.toggleModal(modal, true)
}
</script>
